import { ConnectError } from '@square/web-rpc-client/utils';
import { generateEnvoyConsentHeader, X_ALLOW_COOKIES, X_BLOCK_COOKIES, X_CSRF_Token } from '@squareup/service-one-trust';

function getDefaultRequestHeaders() {
  const headerMap = /* @__PURE__ */ new Map();
  const csrfTokenMeta = document.querySelector("meta[name=csrf-token]");
  const csrfToken = csrfTokenMeta && csrfTokenMeta.getAttribute("content");
  const envoyConsentHeader = generateEnvoyConsentHeader();
  headerMap.set(X_ALLOW_COOKIES, envoyConsentHeader[X_ALLOW_COOKIES]);
  headerMap.set(X_BLOCK_COOKIES, envoyConsentHeader[X_BLOCK_COOKIES]);
  headerMap.set(X_CSRF_Token, csrfToken ?? "");
  return headerMap;
}

async function fetchImpl$1(...args) {
  const [resource, options] = args;
  const headers = new Headers([...getDefaultRequestHeaders()]);
  headers.set("Content-Type", "application/json");
  const customHeaders = new Headers(options?.headers);
  if (customHeaders) {
    customHeaders.forEach((value, key) => {
      headers.append(key, value);
    });
  }
  const res = await fetch(resource, {
    ...options,
    headers
  });
  if (!res.ok) {
    const text = await res.clone().text();
    return Promise.reject(new ConnectError(res, null, text));
  }
  if (res.status === 204) {
    return Promise.resolve(null);
  }
  return res.json();
}

function absolutize(url) {
  if (url[0] === "/") {
    const { protocol, host } = window.location;
    return `${protocol}//${host}${url}`;
  }
  return url;
}

async function fetchImpl(...args) {
  return fetch(...args);
}
async function fetchWithInterceptors(resource, request = {}, interceptors = []) {
  const url = typeof resource === "string" ? absolutize(resource) : resource;
  const reqOptions = { ...request };
  const invokeFetch = (reqOptions2) => fetchImpl(url, reqOptions2);
  if (!interceptors || !interceptors.length) {
    return invokeFetch(reqOptions);
  }
  const next = interceptors.concat().reverse().reduce((n, i) => i(n), invokeFetch);
  return next(reqOptions);
}

const attachBrowserHeaders = (next) => async (req) => {
  const headers = getDefaultRequestHeaders();
  req.headers = new Headers(req.headers || {});
  for (const [key, value] of headers) {
    req.headers.append(key, value);
  }
  return await next(req);
};

const handle204AsNull = (next) => async (req) => {
  const res = await next(req);
  if (res.status === 204) {
    return new Response(JSON.stringify(null), { ...res });
  }
  return res;
};

const handleJSON = (next) => async (req) => {
  req.headers = new Headers(req.headers || {});
  req.headers.append("Accept", "application/json");
  req.headers.append("Content-Type", "application/json");
  const res = await next(req);
  return res.json();
};

const handleLegacyError = (next) => async (req) => {
  const res = await next(req);
  if (!res.ok) {
    const text = await res.clone().text();
    return Promise.reject(new ConnectError(res, null, text));
  }
  return res;
};

async function legacyFetch(...args) {
  const [resource, options] = args;
  return fetchWithInterceptors(resource, options, [handleJSON, handle204AsNull, handleLegacyError, attachBrowserHeaders]);
}

const UPPERCASE = /[\p{Lu}]/u;
const LOWERCASE = /[\p{Ll}]/u;
const LEADING_CAPITAL = /^[\p{Lu}](?![\p{Lu}])/gu;
const IDENTIFIER = /([\p{Alpha}\p{N}_]|$)/u;
const SEPARATORS = /[_.\- ]+/;
const LEADING_SEPARATORS = new RegExp("^" + SEPARATORS.source);
const SEPARATORS_AND_IDENTIFIER = new RegExp(SEPARATORS.source + IDENTIFIER.source, "gu");
const NUMBERS_AND_IDENTIFIER = new RegExp("\\d+" + IDENTIFIER.source, "gu");
const preserveCamelCase = (string, toLowerCase, toUpperCase, preserveConsecutiveUppercase2) => {
  let isLastCharLower = false;
  let isLastCharUpper = false;
  let isLastLastCharUpper = false;
  let isLastLastCharPreserved = false;
  for (let index = 0; index < string.length; index++) {
    const character = string[index];
    isLastLastCharPreserved = index > 2 ? string[index - 3] === "-" : true;
    if (isLastCharLower && UPPERCASE.test(character)) {
      string = string.slice(0, index) + "-" + string.slice(index);
      isLastCharLower = false;
      isLastLastCharUpper = isLastCharUpper;
      isLastCharUpper = true;
      index++;
    } else if (isLastCharUpper && isLastLastCharUpper && LOWERCASE.test(character) && (!isLastLastCharPreserved || preserveConsecutiveUppercase2)) {
      string = string.slice(0, index - 1) + "-" + string.slice(index - 1);
      isLastLastCharUpper = isLastCharUpper;
      isLastCharUpper = false;
      isLastCharLower = true;
    } else {
      isLastCharLower = toLowerCase(character) === character && toUpperCase(character) !== character;
      isLastLastCharUpper = isLastCharUpper;
      isLastCharUpper = toUpperCase(character) === character && toLowerCase(character) !== character;
    }
  }
  return string;
};
const preserveConsecutiveUppercase = (input, toLowerCase) => {
  LEADING_CAPITAL.lastIndex = 0;
  return input.replaceAll(LEADING_CAPITAL, (match) => toLowerCase(match));
};
const postProcess = (input, toUpperCase) => {
  SEPARATORS_AND_IDENTIFIER.lastIndex = 0;
  NUMBERS_AND_IDENTIFIER.lastIndex = 0;
  return input.replaceAll(NUMBERS_AND_IDENTIFIER, (match, pattern, offset) => ["_", "-"].includes(input.charAt(offset + match.length)) ? match : toUpperCase(match)).replaceAll(SEPARATORS_AND_IDENTIFIER, (_, identifier) => toUpperCase(identifier));
};
function camelCase(input, options) {
  if (!(typeof input === "string" || Array.isArray(input))) {
    throw new TypeError("Expected the input to be `string | string[]`");
  }
  options = {
    pascalCase: false,
    preserveConsecutiveUppercase: false,
    ...options
  };
  if (Array.isArray(input)) {
    input = input.map((x) => x.trim()).filter((x) => x.length).join("-");
  } else {
    input = input.trim();
  }
  if (input.length === 0) {
    return "";
  }
  const toLowerCase = options.locale === false ? (string) => string.toLowerCase() : (string) => string.toLocaleLowerCase(options.locale);
  const toUpperCase = options.locale === false ? (string) => string.toUpperCase() : (string) => string.toLocaleUpperCase(options.locale);
  if (input.length === 1) {
    if (SEPARATORS.test(input)) {
      return "";
    }
    return options.pascalCase ? toUpperCase(input) : toLowerCase(input);
  }
  const hasUpperCase = input !== toLowerCase(input);
  if (hasUpperCase) {
    input = preserveCamelCase(input, toLowerCase, toUpperCase, options.preserveConsecutiveUppercase);
  }
  input = input.replace(LEADING_SEPARATORS, "");
  input = options.preserveConsecutiveUppercase ? preserveConsecutiveUppercase(input, toLowerCase) : toLowerCase(input);
  if (options.pascalCase) {
    input = toUpperCase(input.charAt(0)) + input.slice(1);
  }
  return postProcess(input, toUpperCase);
}

const isObject$1 = (value) => typeof value === "object" && value !== null;
const isObjectCustom = (value) => isObject$1(value) && !(value instanceof RegExp) && !(value instanceof Error) && !(value instanceof Date);
const mapObjectSkip = Symbol("mapObjectSkip");
const _mapObject = (object, mapper, options, isSeen = /* @__PURE__ */ new WeakMap()) => {
  options = {
    deep: false,
    target: {},
    ...options
  };
  if (isSeen.has(object)) {
    return isSeen.get(object);
  }
  isSeen.set(object, options.target);
  const { target } = options;
  delete options.target;
  const mapArray = (array) => array.map((element) => isObjectCustom(element) ? _mapObject(element, mapper, options, isSeen) : element);
  if (Array.isArray(object)) {
    return mapArray(object);
  }
  for (const [key, value] of Object.entries(object)) {
    const mapResult = mapper(key, value, object);
    if (mapResult === mapObjectSkip) {
      continue;
    }
    let [newKey, newValue, { shouldRecurse = true } = {}] = mapResult;
    if (newKey === "__proto__") {
      continue;
    }
    if (options.deep && shouldRecurse && isObjectCustom(newValue)) {
      newValue = Array.isArray(newValue) ? mapArray(newValue) : _mapObject(newValue, mapper, options, isSeen);
    }
    target[newKey] = newValue;
  }
  return target;
};
function mapObject(object, mapper, options) {
  if (!isObject$1(object)) {
    throw new TypeError(`Expected an object, got \`${object}\` (${typeof object})`);
  }
  return _mapObject(object, mapper, options);
}

class QuickLRU extends Map {
  #size = 0;
  #cache = /* @__PURE__ */ new Map();
  #oldCache = /* @__PURE__ */ new Map();
  #maxSize;
  #maxAge;
  #onEviction;
  constructor(options = {}) {
    super();
    if (!(options.maxSize && options.maxSize > 0)) {
      throw new TypeError("`maxSize` must be a number greater than 0");
    }
    if (typeof options.maxAge === "number" && options.maxAge === 0) {
      throw new TypeError("`maxAge` must be a number greater than 0");
    }
    this.#maxSize = options.maxSize;
    this.#maxAge = options.maxAge || Number.POSITIVE_INFINITY;
    this.#onEviction = options.onEviction;
  }
  // For tests.
  get __oldCache() {
    return this.#oldCache;
  }
  #emitEvictions(cache) {
    if (typeof this.#onEviction !== "function") {
      return;
    }
    for (const [key, item] of cache) {
      this.#onEviction(key, item.value);
    }
  }
  #deleteIfExpired(key, item) {
    if (typeof item.expiry === "number" && item.expiry <= Date.now()) {
      if (typeof this.#onEviction === "function") {
        this.#onEviction(key, item.value);
      }
      return this.delete(key);
    }
    return false;
  }
  #getOrDeleteIfExpired(key, item) {
    const deleted = this.#deleteIfExpired(key, item);
    if (deleted === false) {
      return item.value;
    }
  }
  #getItemValue(key, item) {
    return item.expiry ? this.#getOrDeleteIfExpired(key, item) : item.value;
  }
  #peek(key, cache) {
    const item = cache.get(key);
    return this.#getItemValue(key, item);
  }
  #set(key, value) {
    this.#cache.set(key, value);
    this.#size++;
    if (this.#size >= this.#maxSize) {
      this.#size = 0;
      this.#emitEvictions(this.#oldCache);
      this.#oldCache = this.#cache;
      this.#cache = /* @__PURE__ */ new Map();
    }
  }
  #moveToRecent(key, item) {
    this.#oldCache.delete(key);
    this.#set(key, item);
  }
  *#entriesAscending() {
    for (const item of this.#oldCache) {
      const [key, value] = item;
      if (!this.#cache.has(key)) {
        const deleted = this.#deleteIfExpired(key, value);
        if (deleted === false) {
          yield item;
        }
      }
    }
    for (const item of this.#cache) {
      const [key, value] = item;
      const deleted = this.#deleteIfExpired(key, value);
      if (deleted === false) {
        yield item;
      }
    }
  }
  get(key) {
    if (this.#cache.has(key)) {
      const item = this.#cache.get(key);
      return this.#getItemValue(key, item);
    }
    if (this.#oldCache.has(key)) {
      const item = this.#oldCache.get(key);
      if (this.#deleteIfExpired(key, item) === false) {
        this.#moveToRecent(key, item);
        return item.value;
      }
    }
  }
  set(key, value, { maxAge = this.#maxAge } = {}) {
    const expiry = typeof maxAge === "number" && maxAge !== Number.POSITIVE_INFINITY ? Date.now() + maxAge : void 0;
    if (this.#cache.has(key)) {
      this.#cache.set(key, {
        value,
        expiry
      });
    } else {
      this.#set(key, { value, expiry });
    }
    return this;
  }
  has(key) {
    if (this.#cache.has(key)) {
      return !this.#deleteIfExpired(key, this.#cache.get(key));
    }
    if (this.#oldCache.has(key)) {
      return !this.#deleteIfExpired(key, this.#oldCache.get(key));
    }
    return false;
  }
  peek(key) {
    if (this.#cache.has(key)) {
      return this.#peek(key, this.#cache);
    }
    if (this.#oldCache.has(key)) {
      return this.#peek(key, this.#oldCache);
    }
  }
  delete(key) {
    const deleted = this.#cache.delete(key);
    if (deleted) {
      this.#size--;
    }
    return this.#oldCache.delete(key) || deleted;
  }
  clear() {
    this.#cache.clear();
    this.#oldCache.clear();
    this.#size = 0;
  }
  resize(newSize) {
    if (!(newSize && newSize > 0)) {
      throw new TypeError("`maxSize` must be a number greater than 0");
    }
    const items = [...this.#entriesAscending()];
    const removeCount = items.length - newSize;
    if (removeCount < 0) {
      this.#cache = new Map(items);
      this.#oldCache = /* @__PURE__ */ new Map();
      this.#size = items.length;
    } else {
      if (removeCount > 0) {
        this.#emitEvictions(items.slice(0, removeCount));
      }
      this.#oldCache = new Map(items.slice(removeCount));
      this.#cache = /* @__PURE__ */ new Map();
      this.#size = 0;
    }
    this.#maxSize = newSize;
  }
  *keys() {
    for (const [key] of this) {
      yield key;
    }
  }
  *values() {
    for (const [, value] of this) {
      yield value;
    }
  }
  *[Symbol.iterator]() {
    for (const item of this.#cache) {
      const [key, value] = item;
      const deleted = this.#deleteIfExpired(key, value);
      if (deleted === false) {
        yield [key, value.value];
      }
    }
    for (const item of this.#oldCache) {
      const [key, value] = item;
      if (!this.#cache.has(key)) {
        const deleted = this.#deleteIfExpired(key, value);
        if (deleted === false) {
          yield [key, value.value];
        }
      }
    }
  }
  *entriesDescending() {
    let items = [...this.#cache];
    for (let i = items.length - 1; i >= 0; --i) {
      const item = items[i];
      const [key, value] = item;
      const deleted = this.#deleteIfExpired(key, value);
      if (deleted === false) {
        yield [key, value.value];
      }
    }
    items = [...this.#oldCache];
    for (let i = items.length - 1; i >= 0; --i) {
      const item = items[i];
      const [key, value] = item;
      if (!this.#cache.has(key)) {
        const deleted = this.#deleteIfExpired(key, value);
        if (deleted === false) {
          yield [key, value.value];
        }
      }
    }
  }
  *entriesAscending() {
    for (const [key, value] of this.#entriesAscending()) {
      yield [key, value.value];
    }
  }
  get size() {
    if (!this.#size) {
      return this.#oldCache.size;
    }
    let oldCacheSize = 0;
    for (const key of this.#oldCache.keys()) {
      if (!this.#cache.has(key)) {
        oldCacheSize++;
      }
    }
    return Math.min(this.#size + oldCacheSize, this.#maxSize);
  }
  get maxSize() {
    return this.#maxSize;
  }
  entries() {
    return this.entriesAscending();
  }
  forEach(callbackFunction, thisArgument = this) {
    for (const [key, value] of this.entriesAscending()) {
      callbackFunction.call(thisArgument, value, key, this);
    }
  }
  get [Symbol.toStringTag]() {
    return "QuickLRU";
  }
  toString() {
    return `QuickLRU(${this.size}/${this.maxSize})`;
  }
  [Symbol.for("nodejs.util.inspect.custom")]() {
    return this.toString();
  }
}

const has = (array, key) => array.some((element) => {
  if (typeof element === "string") {
    return element === key;
  }
  element.lastIndex = 0;
  return element.test(key);
});
const isObject = (value) => typeof value === "object" && value !== null && !(value instanceof RegExp) && !(value instanceof Error) && !(value instanceof Date);

const cache$1 = new QuickLRU({ maxSize: 1e5 });
const transform$1 = (input, options = {}) => {
  if (!isObject(input)) {
    return input;
  }
  const { exclude, excludeChildren, pascalCase = false, stopPaths = [], deep = false, preserveConsecutiveUppercase = false, overrides } = options;
  const stopPathsSet = new Set(stopPaths);
  const mapArray = (input2, parentPath) => input2.map((item) => {
    if (!isObject(item)) {
      return item;
    }
    if (Array.isArray(item)) {
      return mapArray(item, parentPath);
    }
    return mapObject(item, makeMapper(parentPath));
  });
  const makeMapper = (parentPath) => (key, value) => {
    if (deep && isObject(value)) {
      const path = parentPath === void 0 ? String(key) : `${parentPath}.${String(key)}`;
      if (!stopPathsSet.has(path)) {
        value = Array.isArray(value) ? mapArray(value, path) : mapObject(value, makeMapper(path));
      }
    }
    if (excludeChildren && has(excludeChildren, parentPath?.split(".").pop() || "") || exclude && has(exclude, String(key))) {
      return [String(key), value];
    }
    const overrideKey = overrides?.find((override) => typeof override[0] === "string" ? override[0] === String(key) : override[0].test(String(key)));
    if (overrideKey) {
      return [overrideKey[1], value];
    }
    const cacheKey = pascalCase ? `${String(key)}_` : String(key);
    let transformedKey;
    const cached = cache$1.get(cacheKey);
    if (cached !== void 0) {
      transformedKey = cached;
    } else {
      const returnValue = camelCase(String(key), { pascalCase, locale: false, preserveConsecutiveUppercase });
      if (String(key).length < 100) {
        cache$1.set(cacheKey, returnValue);
      }
      transformedKey = returnValue;
    }
    return [transformedKey, value];
  };
  return mapObject(input, makeMapper(void 0));
};
function camelcaseKeys(input, options = {}) {
  if (Array.isArray(input)) {
    return input.map((item) => transform$1(item, options));
  }
  return transform$1(input, options);
}

function handlePreserveConsecutiveUppercase(decamelized, separator) {
  const chars = [...decamelized];
  for (let i = 0; i < chars.length; i++) {
    const char = chars[i];
    if (!/[\p{Uppercase_Letter}\d]/u.test(char)) {
      continue;
    }
    const prevChar = i > 0 ? chars[i - 1] : "";
    const nextChar = i < chars.length - 1 ? chars[i + 1] : "";
    const prevIsUpperOrDigit = /[\p{Uppercase_Letter}\d]/u.test(prevChar);
    const nextIsUpperOrDigit = /[\p{Uppercase_Letter}\d]/u.test(nextChar);
    if (!prevIsUpperOrDigit && !nextIsUpperOrDigit) {
      chars[i] = char.toLowerCase();
    }
  }
  return chars.join("").replace(new RegExp("(\\p{Uppercase_Letter}+)(\\p{Uppercase_Letter}\\p{Lowercase_Letter}+)", "gu"), (_, $1, $2) => $1 + separator + $2.toLowerCase());
}
function decamelize(text, { separator = "_", preserveConsecutiveUppercase = false } = {}) {
  if (!(typeof text === "string" && typeof separator === "string")) {
    throw new TypeError("The `text` and `separator` arguments should be of type `string`");
  }
  if (text.length < 2) {
    return preserveConsecutiveUppercase ? text : text.toLowerCase();
  }
  const replacement = `$1${separator}$2`;
  const decamelized = text.replace(new RegExp("([\\p{Lowercase_Letter}\\d])(\\p{Uppercase_Letter})", "gu"), replacement);
  if (preserveConsecutiveUppercase) {
    return handlePreserveConsecutiveUppercase(decamelized, separator);
  }
  return decamelized.replace(new RegExp("(\\p{Uppercase_Letter})(\\p{Uppercase_Letter}\\p{Lowercase_Letter}+)", "gu"), replacement).toLowerCase();
}

const cache = new QuickLRU({ maxSize: 1e5 });
const transform = (input, options = {}) => {
  if (!isObject(input)) {
    return input;
  }
  const { separator = "_", exclude, excludeChildren, deep = false, preserveConsecutiveUppercase = false, overrides } = options;
  const makeMapper = (parentPath) => (key, value) => {
    if (deep && isObject(value)) {
      const path = parentPath === void 0 ? String(key) : `${parentPath}.${String(key)}`;
      value = mapObject(value, makeMapper(path));
    }
    if (excludeChildren && has(excludeChildren, parentPath?.split(".").pop() || "") || exclude && has(exclude, String(key))) {
      return [String(key), value];
    }
    const overrideKey = overrides?.find((override) => typeof override[0] === "string" ? override[0] === String(key) : override[0].test(String(key)));
    if (overrideKey) {
      return [overrideKey[1], value];
    }
    const cacheKey = `${separator}${String(key)}${preserveConsecutiveUppercase ? 1 : 0}`;
    let transformedKey;
    if (cache.has(cacheKey)) {
      transformedKey = cache.get(cacheKey);
    } else {
      const returnValue = decamelize(String(key), { separator, preserveConsecutiveUppercase });
      if (String(key).length < 100) {
        cache.set(cacheKey, returnValue);
      }
      transformedKey = returnValue;
    }
    return [transformedKey, value];
  };
  return mapObject(input, makeMapper(void 0));
};
function decamelizeKeys(input, options = {}) {
  if (Array.isArray(input)) {
    return Object.keys(input).map((key) => transform(input[key], options));
  }
  return transform(input, options);
}

const HTTP_METHODS_WITH_BODY$2 = ["POST", "PUT", "PATCH"];
const handleCamelizedJSON = (next) => async (req) => {
  req.headers = new Headers(req.headers || {});
  req.headers.append("Accept", "application/json");
  if (!req.headers.has("Content-Type") && !(req.body instanceof FormData)) {
    req.headers.append("Content-Type", "application/json");
  }
  if (req.method !== void 0 && HTTP_METHODS_WITH_BODY$2.includes(req.method) && req.body) {
    if (typeof req.body === "string") {
      const body2 = JSON.parse(req.body);
      const snakeCaseBody = decamelizeKeys(body2, { deep: true });
      req.body = JSON.stringify(snakeCaseBody);
    }
  }
  const res = await next(req);
  if (!res) {
    return res;
  }
  const body = await res.json();
  const camelCaseBody = camelcaseKeys(body, { deep: true });
  return camelCaseBody;
};

async function camelizedJSONFetch(...args) {
  const [resource, options] = args;
  return fetchWithInterceptors(resource, options, [handleCamelizedJSON, handleLegacyError, attachBrowserHeaders]);
}

const HTTP_METHODS_WITH_BODY$1 = ["POST", "PUT", "PATCH"];
const handleCatalogJSON = (next) => async (req) => {
  req.headers = new Headers(req.headers || {});
  req.headers.append("Accept", "application/json");
  if (!req.headers.has("Content-Type") && !(req.body instanceof FormData)) {
    req.headers.append("Content-Type", "application/json");
  }
  if (req.method !== void 0 && HTTP_METHODS_WITH_BODY$1.includes(req.method) && req.body) {
    if (typeof req.body === "string") {
      const body2 = JSON.parse(req.body);
      const snakeCaseBody = decamelizeKeys(body2, {
        deep: true,
        preserveConsecutiveUppercase: true,
        excludeChildren: ["customAttributeValues"],
        overrides: [["catalogV1Ids", "catalog_v1_ids"]]
      });
      req.body = JSON.stringify(snakeCaseBody);
    }
  }
  const res = await next(req);
  if (!res) {
    return res;
  }
  const body = await res.json();
  const camelCaseBody = camelcaseKeys(body, {
    deep: true,
    preserveConsecutiveUppercase: true,
    excludeChildren: ["custom_attribute_values"],
    overrides: [["catalog_v1_ids", "catalogV1Ids"]]
  });
  return camelCaseBody;
};

async function catalogFetch(...args) {
  const [resource, options] = args;
  return fetchWithInterceptors(resource, options, [handleCatalogJSON, handleLegacyError, attachBrowserHeaders]);
}

const HTTP_METHODS_WITH_BODY = ["POST", "PUT", "PATCH"];
const handleKgooseJSON = (next) => async (req) => {
  req.headers = new Headers(req.headers || {});
  req.headers.append("Accept", "application/json");
  if (!req.headers.has("Content-Type") && !(req.body instanceof FormData)) {
    req.headers.append("Content-Type", "application/json");
  }
  if (req.method !== void 0 && HTTP_METHODS_WITH_BODY.includes(req.method) && req.body) {
    if (typeof req.body === "string") {
      const body = JSON.parse(req.body);
      const snakeCaseBody = decamelizeKeys(body, { deep: true });
      req.body = JSON.stringify(snakeCaseBody);
    }
  }
  const res = await next(req);
  if (!res) {
    return res;
  }
  const rawBody = await res.json();
  const camelCaseBody = camelcaseKeys(rawBody, {
    deep: true,
    excludeChildren: ["task_results"]
  });
  return camelCaseBody;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const shouldRetry = (response, retryStatusCodes) => retryStatusCodes.includes(response.status);
const handleRetry = createRetryInterceptor();
function createRetryInterceptor(options = {}) {
  const config = {
    maxRetries: 3,
    retryDelay: 1e3,
    retryStatusCodes: [429, 500, 502, 503, 504],
    ...options
  };
  return (next) => async (reqOptions) => {
    for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
      try {
        const response = await next(reqOptions);
        if (shouldRetry(response, config.retryStatusCodes)) {
          if (attempt === config.maxRetries) {
            throw new Error(`Request failed with status ${response.status} after ${config.maxRetries} retries`);
          }
          await sleep(config.retryDelay);
          continue;
        }
        return response;
      } catch (error) {
        if (attempt === config.maxRetries || !(error instanceof TypeError)) {
          throw error;
        }
        await sleep(config.retryDelay);
      }
    }
    throw new Error("Max retries exceeded");
  };
}

export { attachBrowserHeaders, camelizedJSONFetch, catalogFetch, createRetryInterceptor, fetchImpl$1 as fetch, fetchWithInterceptors, getDefaultRequestHeaders, handle204AsNull, handleCamelizedJSON, handleJSON, handleKgooseJSON, handleLegacyError, handleRetry, legacyFetch };
